import { createKeyPath } from 'CMFW/context/helper/keyContext';
import KeyContext from 'CMFW/context/KeyContext';
import ModuleContext from 'CMFW/context/ModuleContext';
import { SelectLayout } from 'components/selects/SelectLayout';
import { NONE_OPTION } from 'components/selects/selects';
import { IRefIds } from 'constants/refs';
import { getCacheKey } from 'helpers/cache';
import { useCacheData } from 'hooks/useCacheData';
import { useCurrentItemValue } from 'hooks/useCurrentItemValue';
import ActiveInflectTemplateSelectModel, {
  IActiveInflectTemplateSelect,
} from 'models/ActiveInflectTemplateSelectModel';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';

const KEY = 'activeInflectTemplate';

type IProps = {
  isLinked?: boolean;
  className?: string;
  // item key holding the template id (panels: 'activeInflectTemplate')
  keyName?: string;
  label?: string;
  // label of the empty option
  noneLabel?: string;
  // store null instead of NONE_OPTION.id when the empty option is chosen (e.g. compounds)
  nullable?: boolean;
};

/** Templates of the course (cached, shared by every template select/viewer). */
export const useActiveInflectTemplatesData = (courseId: number) => {
  const cacheKey = getCacheKey(IRefIds.activeInflectTemplates, courseId);
  const dataModel = useMemo(() => new ActiveInflectTemplateSelectModel(), []);
  return useCacheData(cacheKey, () => dataModel.get(courseId));
};

const ActiveInflectTemplateSelect: React.FC<IProps> = (props) => {
  const { isLinked, nullable } = props;

  const module = useContext(ModuleContext);

  const parentKey = useContext(KeyContext);
  const aggregatedKeyName = createKeyPath(parentKey, props.keyName || KEY);

  const moduleItemValue = (module.getItemValue.bind(module)(aggregatedKeyName) || NONE_OPTION.id) as number;
  const wordClass = module.getItemUpdatedValue.bind(module)('wordClass') as number;

  const [currentTemplateId, setCurrentTemplateId] = useCurrentItemValue(moduleItemValue, NONE_OPTION.id, isLinked);

  const toItemValue = useCallback(
    (templateId: number) => (nullable && templateId === NONE_OPTION.id ? null : templateId),
    [nullable],
  );

  // Get course ID from module
  const { courseId } = module as any;

  const cacheSelectData = useActiveInflectTemplatesData(courseId);

  // Get word classes to resolve inflectAs
  const wordClassCacheKey = getCacheKey(IRefIds.wordClass, courseId);
  const wordClassesData = useCacheData(wordClassCacheKey, async () => {
    const { default: WordClassSelectModel } = await import('models/WordClassSelectModel');
    const model = new WordClassSelectModel(courseId);
    return model.get();
  });

  // Calculate effective word class (considering inflectAs)
  const effectiveWordClass = useMemo(() => {
    if (!wordClassesData || wordClass === null || wordClass === undefined) {
      return wordClass;
    }
    const currentWordClass = wordClassesData.find((wc) => wc.id === wordClass);
    return currentWordClass?.inflectAs || wordClass;
  }, [wordClassesData, wordClass]);

  // Filter templates by effective word class
  const selectData = useMemo(() => {
    if (cacheSelectData === null || effectiveWordClass === null || effectiveWordClass === undefined) {
      return null;
    }

    const filteredTemplates = cacheSelectData.filter((template) => template.effectiveWordClass === effectiveWordClass);
    return [{ ...NONE_OPTION, name: props.noneLabel || NONE_OPTION.title }, ...filteredTemplates];
  }, [cacheSelectData, effectiveWordClass, props.noneLabel]);

  // Watch for word class changes and reset if current template doesn't match
  useEffect(() => {
    if (currentTemplateId !== NONE_OPTION.id && cacheSelectData && effectiveWordClass !== null && effectiveWordClass !== undefined) {
      const currentTemplate = cacheSelectData.find((t) => t.id === currentTemplateId);
      if (currentTemplate && currentTemplate.effectiveWordClass !== effectiveWordClass) {
        // Reset to NONE if current template doesn't match new effective word class
        setCurrentTemplateId(NONE_OPTION.id);
        module.updateItemValue.bind(module)(aggregatedKeyName, toItemValue(NONE_OPTION.id));
      }
    }
  }, [effectiveWordClass, currentTemplateId, cacheSelectData, module, aggregatedKeyName, toItemValue]);

  const handleChange = (value: IActiveInflectTemplateSelect) => {
    const selectedTemplateId = value.id;
    setCurrentTemplateId(selectedTemplateId);
    module.updateItemValue.bind(module)(aggregatedKeyName, toItemValue(selectedTemplateId));
  };

  return (
    <SelectLayout
      className={props.className}
      data={selectData}
      onChange={handleChange}
      label={props.label || 'Active Inflect Template'}
      optionLabelKey="name"
      currentValue={currentTemplateId}
      originalValue={isLinked && moduleItemValue}
    />
  );
};

export default ActiveInflectTemplateSelect;
